import { CommonModule } from '@angular/common';
import { Component, effect, inject, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { AppSidebar } from '../app-sidebar/app-sidebar';
import { AppTopbar } from '../app-topbar/app-topbar';
import {
  KEYCLOAK_EVENT_SIGNAL,
  KeycloakEventType,
  typeEventArgs,
  ReadyArgs,
} from 'keycloak-angular';
import Keycloak, { KeycloakProfile } from 'keycloak-js';
import { AppNotificationsBar } from '../app-notifications-bar/app-notifications-bar';
import {
  OrganizationConfigService,
  UserGridSettingsService,
} from 'O3_core';
import {
  MessagingOverlay,
  MessagingOrchestratorService,
} from 'O3_communication';
import { ContextMenuService } from 'O3_structure';

@Component({
  selector: 'o3-core-app-layout',
  imports: [
    CommonModule,
    RouterModule,
    AppTopbar,
    AppSidebar,
    AppNotificationsBar,
    MessagingOverlay,
  ],
  templateUrl: './app-layout.html',
  styleUrl: './app-layout.scss',
})
export class AppLayout {
  private readonly keycloak = inject(Keycloak);
  private readonly keycloakSignal = inject(KEYCLOAK_EVENT_SIGNAL);
  private readonly organizationConfigService = inject(
    OrganizationConfigService,
  );
  private readonly userGridSettingsService = inject(UserGridSettingsService);
  private readonly messagingOrchestrator = inject(MessagingOrchestratorService);
  private readonly contextMenuService = inject(ContextMenuService);

  authStatus = signal<boolean | undefined>(undefined);
  userProfile = signal<KeycloakProfile | undefined>(undefined);
  isSidebarCollapsed = false;
  isNotificationsbarCollapsed = true;

  constructor() {
    effect(async () => {
      const keycloakEvent = this.keycloakSignal();

      switch (keycloakEvent.type) {
        case KeycloakEventType.Ready:
          this.keycloakReady(typeEventArgs<ReadyArgs>(keycloakEvent.args));
          break;
        case KeycloakEventType.AuthError:
          console.error('Authentication error:', keycloakEvent.args);
          this.keycloak.logout();
          break;
        case KeycloakEventType.TokenExpired:
          this.keycloakTokenExpired();
          break;
        case KeycloakEventType.AuthRefreshSuccess:
          break;
        case KeycloakEventType.AuthRefreshError:
          console.error('Authentication Refresh error:', keycloakEvent.args);
          break;
        case KeycloakEventType.AuthLogout:
          this.keycloak.logout();
          break;
        default:
          console.warn('Unhandled Keycloak event:', keycloakEvent.type);
          break;
      }
    });
  }

  async keycloakReady(authStatus: boolean) {
    this.authStatus.set(authStatus);

    if (!authStatus) return;

    const profile = await this.keycloak.loadUserProfile();
    this.userProfile.set(profile);
    this.contextMenuService.setUserProfile(profile);

    this.organizationConfigService.loadOrganizationDetails().subscribe();
    void this.userGridSettingsService.ensureLoaded();

    // Initialize global messaging WebSocket connection
    const userId = this.keycloak.tokenParsed?.['sub'] || '';
    const tenantId =
      this.keycloak.tokenParsed?.['tenant_id'] ||
      this.keycloak.tokenParsed?.['tenantId'] ||
      'default';
    if (userId) {
      this.messagingOrchestrator.initialize(userId, tenantId);
    }
  }

  keycloakTokenExpired() {
    this.keycloak
      .updateToken(10)
      .then((refreshed) => {
        if (refreshed) {
          console.log('Token refreshed:', refreshed);
        } else {
          console.log('Token not refreshed, it is still valid');
        }
      })
      .catch(() => {
        console.error(
          'Failed to refresh the token, or the session has expired',
        );
        this.keycloak.logout();
      });
  }

  logout() {
    this.keycloak.logout();
  }

  toggleSidebar() {
    this.isSidebarCollapsed = !this.isSidebarCollapsed;
  }
  toggleNotificationsbar() {
    this.isNotificationsbarCollapsed = !this.isNotificationsbarCollapsed;
  }
}
